import FalseIcon from '@mui/icons-material/Clear'
import TrueIcon from '@mui/icons-material/Done'
import { Box, Typography } from '@mui/material'
import { getReferenceManyFormField, ReferenceManyInput } from '@react-admin/ra-relationships'
import { useTranslate } from 'ra-core'
import { useMemo, useRef } from 'react'
import {
  FormDataConsumer,
  Labeled,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  UPDATE,
  useRecordContext,
} from 'react-admin'
import { useWatch } from 'react-hook-form'

import AdvancedBooleanInput from '../../components/AdvancedBooleanInput'
import { AdvancedDateTimeInput } from '../../components/AdvancedDateTimeInputs'
import BasicFormToolbar from '../../components/BasicFormToolbar'
import PeriodField from '../../components/PeriodField'
import { BOOKING_ERA_CURRENT } from '../../config/bookings'
import { VEHICLE_UNAVAILABILITY_TYPES } from '../../config/vehicleUnavailabilities'
import { isDisabled } from '../../utils'
import {
  getDateWithThirtyMinutesOffset,
  getRoundedNowJS,
  parseDateAsDateTime,
  parseDateAsISO,
  validateEndDateAfterStartDate,
} from '../../utils/dates'
import bookingsConfig from '../bookings/config'
import { FormDivider } from '../common'
import { useCreateResource } from '../common/create'
import { useEditResource } from '../common/edit'
import { AdvancedSimpleForm } from '../common/forms'
import organisationsConfig from '../organisations/config'
import OrganisationReferenceInput from '../organisations/input'
import vehiclesConfig from '../vehicles/config'
import VehicleReferenceInput from '../vehicles/input'

const conflictingBookingsSource = getReferenceManyFormField({
  resource: 'maintenances',
  reference: bookingsConfig.name,
  target: vehiclesConfig.options.referenceKey,
})

const vehicleUnavailabilityTypeChoices = Object.entries(VEHICLE_UNAVAILABILITY_TYPES).map(([k, v]) => ({
  id: k,
  name: v,
}))

const validateEndAfterStart = (value, allValues) => validateEndDateAfterStartDate(allValues.started_on, value)

export const transformValues = (values) => values

const ConflictingBookingsSubFormRoot = ({ children }) => {
  const translate = useTranslate()
  const bookings = useWatch({ name: conflictingBookingsSource })
  const total = bookings?.length

  return (
    <>
      <Box p="4px 10px 20px 10px">
        {total === undefined ? null : total > 0 ? (
          <>
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
              <FalseIcon sx={{ verticalAlign: 'bottom', marginRight: '0.25em', color: 'error.main' }} />
              {translate('resources.maintenances.conflictingBookings.true.title')}
            </Typography>
            <Typography variant="body2" style={{ margin: '13px 13px 0px 30px' }}>
              {translate('resources.maintenances.conflictingBookings.true.helperText')}
            </Typography>
          </>
        ) : (
          <Typography variant="body2">
            <TrueIcon sx={{ verticalAlign: 'bottom', marginRight: '0.25em', color: 'primary.main' }} />
            {translate('resources.maintenances.conflictingBookings.false.title')}
          </Typography>
        )}
      </Box>
      {children}
    </>
  )
}

const ConflictingBookingsSubForm = ({ startedOn, endedOn, vehicleId }) => {
  const record = useMemo(
    () => ({
      [vehiclesConfig.options.referenceKey]: vehicleId,
    }),
    [vehicleId],
  )

  return (
    <ConflictingBookingsSubFormRoot>
      <ReferenceManyInput
        filter={{
          ...bookingsConfig.options.defaultFilterValues,
          timeline_start: parseDateAsISO(startedOn),
          timeline_end: parseDateAsISO(endedOn),
          [vehiclesConfig.options.referenceKey]: vehicleId,
        }}
        label={false}
        queryOptions={{ staleTime: 0 }}
        record={record}
        reference={bookingsConfig.name}
        sort={{ ...bookingsConfig.options.defaultSort, order: 'ASC' }}
        target={vehiclesConfig.options.referenceKey}
        source={vehiclesConfig.options.referenceKey}
      >
        <SimpleFormIterator
          inline
          disableAdd
          disableRemove
          disableReordering
          sx={{
            '& .RaSimpleFormIterator-line': { justifyContent: 'center', marginBottom: '16px' },
            '& .RaSimpleFormIterator-action': { display: 'none' },
          }}
        >
          <FormDataConsumer>
            {({ scopedFormData }) => {
              if (!scopedFormData) return null
              const isBookingCurrent = scopedFormData.era === BOOKING_ERA_CURRENT
              return (
                <>
                  <Labeled label="resources.common.fields.period">
                    <PeriodField
                      record={scopedFormData}
                      startedOnSource="effective_started_on"
                      endedOnSource="effective_ended_on"
                      addTime
                    />
                  </Labeled>
                  <VehicleReferenceInput
                    emptyText="resources.maintenances.conflictingBookings.true.noReallocation"
                    helperText={
                      isBookingCurrent ? 'resources.bookings.forms.helperTexts.conflicting_booking_vehicle_id' : null
                    }
                    filter={{
                      available_from: scopedFormData.start_scheduled_on,
                      available_to: scopedFormData.end_scheduled_on,
                      user_id: scopedFormData.user_id,
                      hub_id: scopedFormData.hub_id,
                    }}
                    format={(v) => (v === vehicleId ? '' : (v ?? ''))}
                    parse={(v) => (v === '' ? vehicleId : v)}
                    sx={{ mt: '3px !important' }}
                    readOnly={isBookingCurrent}
                    linkedToHub={false}
                    validate={null}
                    perPage={50}
                  />
                </>
              )
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ReferenceManyInput>
    </ConflictingBookingsSubFormRoot>
  )
}

const VehicleUnavailabilityFormLayout = ({
  defaultValues = {},
  disabledInputsSources = [],
  type = UPDATE,
  ...props
}) => {
  const record = useRecordContext(props)

  const now = useRef(getRoundedNowJS())
  const end = useRef(getDateWithThirtyMinutesOffset())

  defaultValues = {
    started_on: now.current,
    ended_on: end.current,
    ...defaultValues,
    ...(defaultValues?.organisation_id ? {} : { vehicle_id: undefined }),
  }

  return (
    <AdvancedSimpleForm
      defaultValues={defaultValues}
      record={record}
      toolbar={<BasicFormToolbar formType={type} />}
      type={type}
    >
      <OrganisationReferenceInput
        readOnly={type === UPDATE || isDisabled(disabledInputsSources, organisationsConfig.options.referenceKey)}
      />
      <VehicleReferenceInput
        linkedToHub={false}
        readOnly={type === UPDATE || isDisabled(disabledInputsSources, vehiclesConfig.options.referenceKey)}
      />
      <FormDivider />
      <SelectInput
        choices={vehicleUnavailabilityTypeChoices}
        source="type"
        validate={required()}
        readOnly={type === UPDATE}
      />
      <TextInput multiline source="description" />
      <FormDivider />
      <AdvancedDateTimeInput minDate={now.current} source="started_on" validate={required()} />
      <FormDataConsumer>
        {({ formData }) => (
          <AdvancedDateTimeInput
            minDate={formData.started_on ? parseDateAsDateTime(formData.started_on) : now.current}
            source="ended_on"
            validate={[required(), validateEndAfterStart]}
          />
        )}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) => {
          const {
            started_on: startedOn,
            ended_on: endedOn,
            [vehiclesConfig.options.referenceKey]: vehicleId,
          } = formData

          return startedOn && endedOn && vehicleId ? (
            <>
              <FormDivider />
              <ConflictingBookingsSubForm startedOn={startedOn} endedOn={endedOn} vehicleId={vehicleId} />
            </>
          ) : null
        }}
      </FormDataConsumer>
      <FormDivider />
      <AdvancedBooleanInput source="locked_for_update" />
    </AdvancedSimpleForm>
  )
}

export const useCreateVehicleUnavailability = ({ disabledInputsSources }) =>
  useCreateResource('maintenances', <VehicleUnavailabilityFormLayout />, transformValues, disabledInputsSources)

export const useEditVehicleUnavailability = () =>
  useEditResource('maintenances', <VehicleUnavailabilityFormLayout />, transformValues)

export default VehicleUnavailabilityFormLayout
